import React, { useMemo, useRef } from 'react';
import type { BulkOpsAnalyticsControllerContext } from '@atlassian/jira-bulk-operations-analytics/src/controllers/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { SuccessFlag } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { useFlagAnalytics } from '../../../../common/ui/use-flag-analytics/index.tsx';
import {
	useFormSubmitFlagData,
	useFormSubmitIssueIds,
	useFormSubmitSuccessData,
} from '../../../../controllers/use-form-submit-state/main.tsx';
import { TEST_ID, FLAG_ID } from './constants.tsx';
import messages from './messages.tsx';

type Props = {
	onDismissed: () => void;
	bulkOpsFlow: BulkOpsAnalyticsControllerContext;
} & FlagCustomRenderProps;

export const OperationSuccessFlag = ({ onDismissed, bulkOpsFlow, ...props }: Props) => {
	const { formatMessage } = useIntl();

	const flagData = useFormSubmitFlagData();
	const issueIds = useFormSubmitIssueIds();
	const count = useRef(useFormSubmitSuccessData());

	const analyticsAttributes = useMemo(
		() => ({
			numSelectedCards: issueIds.length,
			flagType: 'success',
			numCardsUpdated: count.current,
		}),
		[issueIds.length],
	);

	useFlagAnalytics(analyticsAttributes, bulkOpsFlow);

	const flagDescription = useMemo(() => {
		if (flagData && flagData.customDescription && expVal('bulk_watch_unwatch', 'isEnabled', false))
			return flagData.customDescription;
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false))
			return formatMessage(messages.descriptionIssueTermRefresh, { count: count.current });
		return formatMessage(messages.description, { count: count.current });
	}, [flagData, formatMessage]);

	return (
		<SuccessFlag
			{...props}
			testId={TEST_ID}
			id={FLAG_ID}
			title={formatMessage(messages.title)}
			description={flagDescription}
			onDismissed={onDismissed}
			messageId="bulk-operations-common.ui.submit-status.flags.success-flag"
			messageType="transactional"
		/>
	);
};
